import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { unifiedApi } from '../../api/unifiedApi';
import { unlockGlobally } from '../../store/slices/lockSessionSlice';
import { useKeyboardState } from '../../hooks/useMobile';
import ForgetPasswordModal from './ForgetPasswordModal';
import { useLogout } from '../../auth/hooks';

interface NoteLockOverlayProps {
  noteId: string;
  noteTitle: string;
  onUnlock?: () => void;
  onForgetPassword?: () => void;
}

// 四个格子密码输入组件
interface PinInputProps {
  onComplete: (pin: string) => void;
  onChange?: (pin: string) => void;
  error?: boolean;
}

const PinInput: React.FC<PinInputProps> = ({ onComplete, onChange, error }) => {
  const [pin, setPin] = useState(['', '', '', '']);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // 聚焦到指定索引的输入框
  const focusInput = useCallback((index: number) => {
    if (index >= 0 && index < 4 && inputRefs.current[index]) {
      inputRefs.current[index]?.focus();
    }
  }, []);

  // 处理输入
  const handleInputChange = useCallback((index: number, value: string) => {
    // 允许字母和数字
    const char = value.replace(/[^a-zA-Z0-9]/g, '').slice(-1);

    if (char) {
      const newPin = [...pin];
      newPin[index] = char;
      setPin(newPin);

      const currentPin = newPin.join('');
      onChange?.(currentPin);

      // 如果当前格子已填且不是最后一个，自动聚焦到下一个
      if (index < 3) {
        focusInput(index + 1);
      } else {
        // 最后一个格子输入完成，触发完成回调
        if (currentPin.length === 4) {
          onComplete(currentPin);
        }
      }
    }
  }, [pin, onComplete, onChange, focusInput]);

  // 处理键盘事件
  const handleKeyDown = useCallback((index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace') {
      if (pin[index]) {
        // 当前格子有内容，清空它
        const newPin = [...pin];
        newPin[index] = '';
        setPin(newPin);
        onChange?.(newPin.join(''));
      } else if (index > 0) {
        // 当前格子为空，回到上一个格子
        const newPin = [...pin];
        newPin[index - 1] = '';
        setPin(newPin);
        onChange?.(newPin.join(''));
        focusInput(index - 1);
      }
    } else if (e.key === 'ArrowLeft' && index > 0) {
      focusInput(index - 1);
    } else if (e.key === 'ArrowRight' && index < 3) {
      focusInput(index + 1);
    }
  }, [pin, onChange, focusInput]);

  // 处理粘贴
  const handlePaste = useCallback((e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/[^a-zA-Z0-9]/g, '').slice(0, 4);

    if (pastedData) {
      const newPin = pastedData.split('').concat(['', '', '', '']).slice(0, 4);
      setPin(newPin);
      onChange?.(newPin.join(''));

      if (pastedData.length === 4) {
        onComplete(pastedData);
      } else {
        focusInput(pastedData.length);
      }
    }
  }, [onComplete, onChange, focusInput]);

  // 当 error 变化时，如果有错误，聚焦第一个输入框
  useEffect(() => {
    if (error) {
      setPin(['', '', '', '']);
      focusInput(0);
    }
  }, [error, focusInput]);

  return (
    <div className="flex items-center justify-center gap-3">
      {pin.map((digit, index) => (
        <input
          key={index}
          ref={(el) => { inputRefs.current[index] = el; }}
          type="password"
          maxLength={1}
          value={digit}
          onChange={(e) => handleInputChange(index, e.target.value)}
          onKeyDown={(e) => handleKeyDown(index, e)}
          onPaste={handlePaste}
          className={`
            w-12 h-14 text-center text-2xl font-semibold rounded-lg
            border-2 transition-all duration-200 outline-none
            ${error 
              ? 'border-red-500 bg-red-50 text-red-600 animate-shake' 
              : digit 
                ? 'border-blue-500 bg-blue-50 text-blue-700' 
                : 'border-gray-300 bg-white text-gray-800 hover:border-gray-400'
            }
            focus:border-blue-500 focus:ring-2 focus:ring-blue-200
            dark:bg-gray-800 dark:text-white
            ${error ? 'dark:bg-red-900/20 dark:border-red-500' : ''}
            ${digit && !error ? 'dark:bg-blue-900/20 dark:border-blue-500' : ''}
          `}
          autoFocus={index === 0}
        />
      ))}
    </div>
  );
};

const NoteLockOverlay: React.FC<NoteLockOverlayProps> = ({
  noteId,
  noteTitle,
  onUnlock,
  onForgetPassword,
}) => {
  const dispatch = useDispatch();
  const { logout } = useLogout();
  const [error, setError] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [currentPin, setCurrentPin] = useState('');
  const [showForgetModal, setShowForgetModal] = useState(false);

  // ★ 键盘适配（2026-10-29）：复用全局多通道键盘检测（visualViewport 缩小 / 原生 IME 边衬区）
  // 键盘弹出时用真实键盘底缘高度作为容器 paddingBottom，解锁卡片在"可视区域"内
  // 垂直居中，PIN 输入格始终位于键盘上方；桌面无键盘时无影响
  const { isOpen: keyboardOpen, toolbarBottom: keyboardBottom } = useKeyboardState();

  // 验证密码
  const verifyAndUnlock = useCallback(async (pin: string) => {
    if (pin.length !== 4 || isVerifying) return;

    setIsVerifying(true);
    setError(false);

    try {
      console.log('[NoteLockOverlay] 验证密码:', pin);
      const isValid = await unifiedApi.verifyLockPassword(pin);
      console.log('[NoteLockOverlay] 验证结果:', isValid);

      if (isValid) {
        // 触发全局解锁
        dispatch(unlockGlobally());
        // 调用可选的回调
        onUnlock?.();
      } else {
        setError(true);
        setCurrentPin('');
      }
    } catch (err) {
      console.error('[NoteLockOverlay] 验证失败:', err);
      setError(true);
      setCurrentPin('');
    } finally {
      setIsVerifying(false);
    }
  }, [dispatch, onUnlock, isVerifying]);

  // 处理 PIN 变化
  const handlePinChange = useCallback((pin: string) => {
    setCurrentPin(pin);
    if (error) {
      setError(false);
    }
  }, [error]);

  // 处理忘记密码确认
  const handleForgetPasswordConfirm = useCallback(async () => {
    try {
      // 步骤1: 设置 from_forget_password 标记（必须在登出之前设置）
      localStorage.setItem('from_forget_password', 'true');

      // 步骤2: 关闭弹窗
      setShowForgetModal(false);

      // 步骤3: 调用 useLogout 执行完整登出流程（包括 Casdoor 服务端登出）
      await logout();
    } catch (err) {
      console.error('[NoteLockOverlay] 处理忘记密码失败:', err);
    }
  }, [logout]);

  return (
    <>
      <div
        className="absolute inset-0 z-50 flex flex-col items-center bg-white dark:bg-gray-900 overflow-y-auto"
        style={keyboardOpen && keyboardBottom > 0 ? { paddingBottom: `${Math.round(keyboardBottom) + 16}px` } : undefined}
      >
        {/* my-auto：flex 安全居中（空间不足时贴顶可滚动，避免 justify-center 溢出裁切） */}
        <div className="my-auto bg-white dark:bg-gray-800 rounded-xl shadow-2xl p-8 w-80 border border-gray-200 dark:border-gray-700">
          <div className="flex flex-col items-center text-center">
            <div className="w-16 h-16 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center mb-4">
              <svg className="w-8 h-8 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
            </div>

            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">
              笔记已上锁
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-2 truncate max-w-full">
              {noteTitle}
            </p>
            <p className="text-xs text-gray-400 dark:text-gray-500 mb-6">
              解锁后将解锁所有已上锁笔记
            </p>

            {/* 四个格子密码输入 */}
            <div className="w-full mb-4">
              <PinInput
                onComplete={verifyAndUnlock}
                onChange={handlePinChange}
                error={error}
              />
            </div>

            {/* 错误提示 */}
            {error && (
              <p className="text-sm text-red-500 animate-pulse mb-4">
                密码错误，请重试
              </p>
            )}

            {/* 加载状态 */}
            {isVerifying && (
              <div className="flex items-center gap-2 text-blue-500">
                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                <span className="text-sm">验证中...</span>
              </div>
            )}

            {/* 忘记密码链接 */}
            <button
              onClick={() => setShowForgetModal(true)}
              className="mt-4 text-sm text-blue-500 hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-300 transition-colors"
            >
              忘记密码？ 修改密码
            </button>
          </div>
        </div>
      </div>

      {/* 忘记密码确认弹窗 */}
      <ForgetPasswordModal
        isOpen={showForgetModal}
        onClose={() => setShowForgetModal(false)}
        onConfirm={handleForgetPasswordConfirm}
      />
    </>
  );
};

export default NoteLockOverlay;
